import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  FileInput,
  Receipt,
  Clock,
  Settings2,
  GripVertical,
  Eye,
  EyeOff,
  RotateCcw,
  Landmark,
  Plus
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  ComposedChart,
  Line
} from "recharts";

// Format currency in Italian style
const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

// Format date safely - handles empty strings and invalid dates
const formatDate = (dateStr, options = {}) => {
  if (!dateStr || dateStr === '') return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('it-IT', options);
};

// Default widgets configuration
const DEFAULT_WIDGETS = [
  { id: "kpi_saldo_cassa", name: "Saldo Cassa", enabled: true, order: 1, size: "sm" },
  { id: "kpi_da_incassare", name: "Da Incassare", enabled: true, order: 2, size: "sm" },
  { id: "kpi_da_pagare", name: "Da Pagare", enabled: true, order: 3, size: "sm" },
  { id: "kpi_saldo_totale", name: "Saldo Totale", enabled: true, order: 4, size: "sm" },
  { id: "kpi_saldo_banca", name: "Saldo Totale Banche", enabled: true, order: 5, size: "sm" },
  { id: "kpi_costi_funerali", name: "Costi Funerali", enabled: true, order: 6, size: "sm" },
  { id: "kpi_fatture_scadute", name: "Fatture Scadute", enabled: true, order: 7, size: "sm" },
  { id: "kpi_clienti_totali", name: "Clienti Totali", enabled: true, order: 8, size: "sm" },
  { id: "kpi_clienti_rischio", name: "Clienti a Rischio", enabled: false, order: 9, size: "sm" },
  { id: "chart_conti_bancari", name: "Conti Bancari", enabled: true, order: 10, size: "md" },
  { id: "chart_trend_mensile", name: "Andamento Mensile", enabled: true, order: 11, size: "lg" },
  { id: "chart_costi_categoria", name: "Costi per Categoria", enabled: true, order: 12, size: "md" },
  { id: "list_movimenti_banca", name: "Movimenti Bancari Recenti", enabled: true, order: 13, size: "md" },
  { id: "list_fatture_incassare", name: "Fatture da Incassare", enabled: true, order: 14, size: "md" },
  { id: "list_fatture_pagare", name: "Fatture da Pagare", enabled: true, order: 15, size: "md" },
  { id: "list_costi_recenti", name: "Costi Recenti", enabled: true, order: 16, size: "md" },
  { id: "list_top_fornitori", name: "Top Fornitori", enabled: true, order: 17, size: "md" },
  { id: "list_clienti_rischio", name: "Lista Clienti a Rischio", enabled: false, order: 18, size: "md" }
];

const COLORS = ['#007AFF', '#34C759', '#FF9500', '#AF52DE', '#FF2D55'];

const Dashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [showConfig, setShowConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => {
    fetchData();
    fetchWidgetConfig();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, cashflowRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, getAuthHeaders()),
        axios.get(`${API}/cashflow`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      setCashflow(cashflowRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const fetchWidgetConfig = async () => {
    try {
      const response = await axios.get(`${API}/settings`, getAuthHeaders());
      if (response.data?.dashboard_widgets?.length > 0) {
        setWidgets(response.data.dashboard_widgets);
      }
    } catch (error) {
      // Use default widgets if settings not found
    }
  };

  const saveWidgetConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.post(`${API}/settings`, { dashboard_widgets: widgets }, getAuthHeaders());
      toast.success("Configurazione dashboard salvata");
      setShowConfig(false);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleWidget = (widgetId) => {
    setWidgets(prev => prev.map(w => 
      w.id === widgetId ? { ...w, enabled: !w.enabled } : w
    ));
  };

  const moveWidget = (widgetId, direction) => {
    const index = widgets.findIndex(w => w.id === widgetId);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === widgets.length - 1)) return;
    
    const newWidgets = [...widgets];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newWidgets[index], newWidgets[newIndex]] = [newWidgets[newIndex], newWidgets[index]];
    
    // Update order numbers
    newWidgets.forEach((w, i) => w.order = i + 1);
    setWidgets(newWidgets);
  };

  const resetWidgets = () => {
    setWidgets(DEFAULT_WIDGETS);
  };

  const isWidgetEnabled = (widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    return widget?.enabled ?? true;
  };

  const getWidgetOrder = (widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    return widget?.order ?? 999;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const pieData = stats?.costs_by_category 
    ? Object.entries(stats.costs_by_category).map(([name, value]) => ({ name, value }))
    : [];

  // Widget Components
  const WidgetKpiSaldoCassa = () => (
    <Card className="stat-card card-hover" data-testid="balance-card">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="stat-card-label">Saldo Cassa</p>
            <p className="stat-card-value">{formatCurrency(stats?.current_balance || 0)}</p>
            <p className={`stat-card-trend ${stats?.current_balance >= 0 ? 'positive' : 'negative'}`}>
              {stats?.current_balance >= 0 ? (
                <><ArrowUpRight className="inline w-4 h-4" /> Positivo</>
              ) : (
                <><ArrowDownRight className="inline w-4 h-4" /> Negativo</>
              )}
            </p>
          </div>
          <div className="w-12 h-12 bg-[#007AFF]/10 rounded-2xl flex items-center justify-center">
            <Wallet className="w-6 h-6 text-[#007AFF]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const WidgetKpiDaIncassare = () => (
    <Card className="stat-card card-hover" data-testid="fatture-attive-card">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="stat-card-label">Da Incassare</p>
            <p className="stat-card-value text-[#34C759]">{formatCurrency(stats?.fatture_attive?.da_incassare || 0)}</p>
            <p className="text-sm text-[#86868B] mt-1">
              {stats?.fatture_attive?.count || 0} fatture attive
            </p>
          </div>
          <div className="w-12 h-12 bg-[#34C759]/10 rounded-2xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-[#34C759]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const WidgetKpiDaPagare = () => (
    <Card className="stat-card card-hover" data-testid="fatture-passive-card">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="stat-card-label">Da Pagare</p>
            <p className="stat-card-value text-[#FF3B30]">{formatCurrency(stats?.fatture_passive?.da_pagare || 0)}</p>
            <p className="text-sm text-[#86868B] mt-1">
              {stats?.fatture_passive?.count || 0} fatture passive
            </p>
          </div>
          <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-2xl flex items-center justify-center">
            <FileInput className="w-6 h-6 text-[#FF3B30]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const WidgetKpiSaldoTotale = () => (
    <Card className="stat-card card-hover" data-testid="saldo-totale-card">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="stat-card-label">Saldo Totale</p>
            <p className={`stat-card-value ${(stats?.saldo_totale || 0) >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
              {formatCurrency(stats?.saldo_totale || 0)}
            </p>
            <p className="text-sm text-[#86868B] mt-1">
              Cassa + Crediti - Debiti
            </p>
          </div>
          <div className={`w-12 h-12 ${(stats?.saldo_totale || 0) >= 0 ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'} rounded-2xl flex items-center justify-center`}>
            {(stats?.saldo_totale || 0) >= 0 ? (
              <TrendingUp className="w-6 h-6 text-[#34C759]" />
            ) : (
              <TrendingDown className="w-6 h-6 text-[#FF3B30]" />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const WidgetKpiCostiFunerali = () => (
    <Card className="stat-card card-hover">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF9500]/10 rounded-xl flex items-center justify-center">
            <Receipt className="w-5 h-5 text-[#FF9500]" />
          </div>
          <div>
            <p className="text-xs text-[#86868B] uppercase tracking-wider">Costi Funerali</p>
            <p className="text-xl font-semibold text-[#1D1D1F]">{formatCurrency(stats?.total_costs || 0)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const WidgetKpiSaldoBanca = () => (
    <Card className="stat-card card-hover" data-testid="bank-balance-card">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#007AFF]/10 rounded-xl flex items-center justify-center">
            <Landmark className="w-5 h-5 text-[#007AFF]" />
          </div>
          <div>
            <p className="text-xs text-[#86868B] uppercase tracking-wider">Saldo Totale Banche</p>
            {stats?.total_bank_balance !== undefined ? (
              <p className={`text-xl font-semibold ${
                stats.total_bank_balance >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
              }`}>
                {formatCurrency(stats.total_bank_balance)}
              </p>
            ) : stats?.bank_statements?.current_balance !== null ? (
              <p className={`text-xl font-semibold ${
                stats.bank_statements.current_balance >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
              }`}>
                {formatCurrency(stats.bank_statements.current_balance)}
              </p>
            ) : (
              <p className="text-sm text-[#86868B]">Nessun conto configurato</p>
            )}
            {stats?.bank_accounts?.length > 0 && (
              <p className="text-xs text-[#86868B] mt-1">
                {stats.bank_accounts.length} conti attivi
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const WidgetKpiFattureScadute = () => (
    <Card className="stat-card card-hover">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center">
            <Clock className="w-5 h-5 text-[#FF3B30]" />
          </div>
          <div>
            <p className="text-xs text-[#86868B] uppercase tracking-wider">Fatture Scadute</p>
            <p className="text-xl font-semibold text-[#FF3B30]">
              {formatCurrency((stats?.fatture_attive?.scadute || 0) + (stats?.fatture_passive?.scadute || 0))}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const WidgetKpiClientiTotali = () => (
    <Card className="stat-card card-hover">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#AF52DE]/10 rounded-xl flex items-center justify-center">
            <Users className="w-5 h-5 text-[#AF52DE]" />
          </div>
          <div>
            <p className="text-xs text-[#86868B] uppercase tracking-wider">Clienti Totali</p>
            <p className="text-xl font-semibold text-[#1D1D1F]">{stats?.total_clients || 0}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const WidgetKpiClientiRischio = () => (
    <Card className="stat-card card-hover">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF9500]/10 rounded-xl flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-[#FF9500]" />
          </div>
          <div>
            <p className="text-xs text-[#86868B] uppercase tracking-wider">Clienti a Rischio</p>
            <p className="text-xl font-semibold text-[#FF9500]">{stats?.risky_clients || 0}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const WidgetChartTrendMensile = () => (
    <Card className="lg:col-span-2 bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
          Andamento Ultimi 6 Mesi
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]" data-testid="monthly-trend-chart">
          {stats?.monthly_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={stats.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="month_name" stroke="#86868B" fontSize={12} />
                <YAxis tickFormatter={(value) => `€${(value/1000).toFixed(0)}k`} stroke="#86868B" fontSize={12} />
                <Tooltip 
                  formatter={(value, name) => {
                    const labels = {
                      fatture_attive: 'Fatture Attive',
                      fatture_passive: 'Fatture Passive',
                      costi_funerali: 'Costi Funerali',
                      margine: 'Margine'
                    };
                    return [formatCurrency(value), labels[name] || name];
                  }}
                  contentStyle={{ 
                    background: 'white', 
                    border: 'none', 
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend 
                  formatter={(value) => ({
                    fatture_attive: 'Fatture Attive',
                    fatture_passive: 'Fatture Passive',
                    costi_funerali: 'Costi Funerali',
                    margine: 'Margine'
                  }[value] || value)}
                />
                <Bar dataKey="fatture_attive" fill="#34C759" radius={[4, 4, 0, 0]} />
                <Bar dataKey="fatture_passive" fill="#FF3B30" radius={[4, 4, 0, 0]} />
                <Bar dataKey="costi_funerali" fill="#FF9500" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="margine" stroke="#007AFF" strokeWidth={3} dot={{ fill: '#007AFF', r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-[#86868B]">
              Nessun dato disponibile
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const WidgetChartCostiCategoria = () => (
    <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
          Costi per Categoria
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[200px]" data-testid="costs-pie-chart">
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ 
                    background: 'white', 
                    border: 'none', 
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-[#86868B]">
              Nessun dato disponibile
            </div>
          )}
        </div>
        <div className="mt-2 space-y-2">
          {pieData.slice(0, 4).map((item, index) => (
            <div key={item.name} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-[#1D1D1F] truncate">{item.name}</span>
              </div>
              <span className="text-[#86868B] font-medium">{formatCurrency(item.value)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const WidgetChartContiBancari = () => (
    <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]" data-testid="bank-accounts-widget">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2">
          <Landmark className="w-5 h-5 text-[#007AFF]" />
          Conti Bancari
        </CardTitle>
        <Link to="/conti-bancari">
          <Button variant="ghost" size="sm" className="text-[#007AFF] hover:bg-[#007AFF]/10">
            Gestisci
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {stats?.bank_accounts?.length > 0 ? (
          <div className="space-y-3">
            {stats.bank_accounts.map((account) => (
              <div 
                key={account.id} 
                className="p-3 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors"
                style={{ borderLeftWidth: '4px', borderLeftColor: account.color }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${account.color}20` }}
                    >
                      <Landmark className="w-4 h-4" style={{ color: account.color }} />
                    </div>
                    <div>
                      <p className="font-medium text-[#1D1D1F] text-sm">{account.name}</p>
                      <p className="text-xs text-[#86868B]">{account.bank_name}</p>
                    </div>
                  </div>
                  <p className={`font-bold ${account.balance >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                    {formatCurrency(account.balance)}
                  </p>
                </div>
                <div className="flex justify-between text-xs text-[#86868B]">
                  <span className="flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3 text-[#34C759]" />
                    {formatCurrency(account.income)}
                  </span>
                  <span className="flex items-center gap-1">
                    <ArrowDownRight className="w-3 h-3 text-[#FF3B30]" />
                    {formatCurrency(account.expense)}
                  </span>
                  <span>{account.movements_count} mov.</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Landmark className="w-12 h-12 mx-auto text-[#86868B] mb-3" />
            <p className="text-[#86868B] mb-3">Nessun conto bancario configurato</p>
            <Link to="/conti-bancari">
              <Button size="sm" className="bg-[#007AFF] hover:bg-[#0056b3]">
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi Conto
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const WidgetListFattureIncassare = () => (
    <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
          Fatture da Incassare
        </CardTitle>
        <Link to="/fatture-attive">
          <Button variant="ghost" size="sm" className="text-[#007AFF] hover:bg-[#007AFF]/10">
            Vedi tutte
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {stats?.fatture_attive?.recenti?.length > 0 ? (
          <div className="space-y-3">
            {stats.fatture_attive.recenti.map((fattura) => (
              <div key={fattura.id} className="flex items-center justify-between p-3 bg-[#F5F5F7]/50 rounded-xl hover:bg-[#F5F5F7] transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${fattura.stato === 'scaduta' ? 'bg-[#FF3B30]' : 'bg-[#34C759]'}`} />
                  <div>
                    <p className="font-medium text-[#1D1D1F]">{fattura.cliente}</p>
                    <p className="text-sm text-[#86868B]">
                      {fattura.numero} • Scad. {formatDate(fattura.data_scadenza)}
                    </p>
                  </div>
                </div>
                <p className="font-semibold text-[#34C759]">
                  {formatCurrency(fattura.totale)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#86868B] text-center py-8">Nessuna fattura da incassare</p>
        )}
      </CardContent>
    </Card>
  );

  const WidgetListMovimentiBanca = () => (
    <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]" data-testid="bank-movements-widget">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2">
          <Landmark className="w-5 h-5 text-[#007AFF]" />
          Movimenti Bancari Recenti
        </CardTitle>
        <Link to="/import-export">
          <Button variant="ghost" size="sm" className="text-[#007AFF] hover:bg-[#007AFF]/10">
            Importa
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {stats?.bank_statements?.recent_entries?.length > 0 ? (
          <div className="space-y-3">
            {stats.bank_statements.recent_entries.map((entry, index) => (
              <div key={entry.id || index} className="flex items-center justify-between p-3 bg-[#F5F5F7]/50 rounded-xl hover:bg-[#F5F5F7] transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    entry.amount >= 0 ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'
                  }`}>
                    {entry.amount >= 0 ? (
                      <ArrowUpRight className="w-4 h-4 text-[#34C759]" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4 text-[#FF3B30]" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#1D1D1F] truncate max-w-[200px]">
                      {entry.description?.substring(0, 40) || 'Movimento'}
                      {entry.description?.length > 40 ? '...' : ''}
                    </p>
                    <p className="text-sm text-[#86868B]">
                      {formatDate(entry.date)} • {entry.category || 'Altro'}
                    </p>
                  </div>
                </div>
                <p className={`font-semibold ${entry.amount >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                  {entry.amount >= 0 ? '+' : ''}{formatCurrency(entry.amount)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Landmark className="w-12 h-12 text-[#86868B] mx-auto mb-3" />
            <p className="text-[#86868B] mb-2">Nessun estratto conto importato</p>
            <Link to="/import-export">
              <Button variant="outline" size="sm" className="text-[#007AFF] border-[#007AFF]">
                Importa Estratto Conto
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const WidgetListFatturePagare = () => (
    <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
          Fatture da Pagare
        </CardTitle>
        <Link to="/fatture-passive">
          <Button variant="ghost" size="sm" className="text-[#FF3B30] hover:bg-[#FF3B30]/10">
            Vedi tutte
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {stats?.fatture_passive?.recenti?.length > 0 ? (
          <div className="space-y-3">
            {stats.fatture_passive.recenti.map((fattura) => (
              <div key={fattura.id} className="flex items-center justify-between p-3 bg-[#F5F5F7]/50 rounded-xl hover:bg-[#F5F5F7] transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${fattura.stato === 'scaduta' ? 'bg-[#FF3B30]' : 'bg-[#FF9500]'}`} />
                  <div>
                    <p className="font-medium text-[#1D1D1F]">{fattura.fornitore}</p>
                    <p className="text-sm text-[#86868B]">
                      {fattura.numero} • Scad. {formatDate(fattura.data_scadenza)}
                    </p>
                  </div>
                </div>
                <p className="font-semibold text-[#FF3B30]">
                  -{formatCurrency(fattura.totale)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#86868B] text-center py-8">Nessuna fattura da pagare</p>
        )}
      </CardContent>
    </Card>
  );

  const WidgetListCostiRecenti = () => (
    <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
          Costi Recenti
        </CardTitle>
        <Link to="/costs">
          <Button variant="ghost" size="sm" className="text-[#FF9500] hover:bg-[#FF9500]/10">
            Vedi tutti
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {stats?.recent_costs?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_costs.map((cost) => (
              <div key={cost.id} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
                <div>
                  <p className="font-medium text-[#1D1D1F]">{cost.description}</p>
                  <p className="text-sm text-[#86868B]">{cost.category}</p>
                </div>
                <p className="font-semibold text-[#FF9500]">
                  -{formatCurrency(cost.amount)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#86868B] text-center py-8">Nessun costo registrato</p>
        )}
      </CardContent>
    </Card>
  );

  const WidgetListTopFornitori = () => (
    <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
          Top Fornitori
        </CardTitle>
      </CardHeader>
      <CardContent>
        {stats?.fatture_passive?.top_fornitori?.length > 0 ? (
          <div className="space-y-3">
            {stats.fatture_passive.top_fornitori.map((fornitore, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#F5F5F7] rounded-full flex items-center justify-center text-sm font-semibold text-[#86868B]">
                    {i + 1}
                  </div>
                  <p className="font-medium text-[#1D1D1F]">{fornitore.fornitore}</p>
                </div>
                <p className="font-semibold text-[#1D1D1F]">
                  {formatCurrency(fornitore.totale)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#86868B] text-center py-8">Nessun fornitore</p>
        )}
      </CardContent>
    </Card>
  );

  const WidgetListClientiRischio = () => (
    <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
          Clienti a Rischio
        </CardTitle>
        <Link to="/clients">
          <Button variant="ghost" size="sm" className="text-[#AF52DE] hover:bg-[#AF52DE]/10">
            Vedi tutti
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {stats?.risky_clients_list?.length > 0 ? (
          <div className="space-y-3">
            {stats.risky_clients_list.map((client) => (
              <div key={client.id} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
                <div>
                  <p className="font-medium text-[#1D1D1F]">{client.name}</p>
                  <p className="text-sm text-[#86868B]">
                    {formatCurrency(client.total_credit)} • {client.days_overdue} gg ritardo
                  </p>
                </div>
                <div className={`risk-badge ${
                  (client.risk_score || client.days_overdue * 2) > 70 ? 'high' : 
                  (client.risk_score || client.days_overdue * 2) > 40 ? 'medium' : 'low'
                }`}>
                  {Math.round(client.risk_score || client.days_overdue * 2)}%
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#86868B] text-center py-8">Nessun cliente a rischio</p>
        )}
      </CardContent>
    </Card>
  );

  // Map widget IDs to components
  const widgetComponents = {
    kpi_saldo_cassa: WidgetKpiSaldoCassa,
    kpi_da_incassare: WidgetKpiDaIncassare,
    kpi_da_pagare: WidgetKpiDaPagare,
    kpi_saldo_totale: WidgetKpiSaldoTotale,
    kpi_saldo_banca: WidgetKpiSaldoBanca,
    kpi_costi_funerali: WidgetKpiCostiFunerali,
    kpi_fatture_scadute: WidgetKpiFattureScadute,
    kpi_clienti_totali: WidgetKpiClientiTotali,
    kpi_clienti_rischio: WidgetKpiClientiRischio,
    chart_conti_bancari: WidgetChartContiBancari,
    chart_trend_mensile: WidgetChartTrendMensile,
    chart_costi_categoria: WidgetChartCostiCategoria,
    list_movimenti_banca: WidgetListMovimentiBanca,
    list_fatture_incassare: WidgetListFattureIncassare,
    list_fatture_pagare: WidgetListFatturePagare,
    list_costi_recenti: WidgetListCostiRecenti,
    list_top_fornitori: WidgetListTopFornitori,
    list_clienti_rischio: WidgetListClientiRischio
  };

  // Sort and filter widgets
  const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);
  const kpiWidgetsPrimary = sortedWidgets.filter(w => ['kpi_saldo_cassa', 'kpi_da_incassare', 'kpi_da_pagare', 'kpi_saldo_totale'].includes(w.id) && w.enabled);
  const kpiWidgetsSecondary = sortedWidgets.filter(w => ['kpi_saldo_banca', 'kpi_costi_funerali', 'kpi_fatture_scadute', 'kpi_clienti_totali', 'kpi_clienti_rischio'].includes(w.id) && w.enabled);
  const chartWidgets = sortedWidgets.filter(w => w.id.startsWith('chart_') && w.enabled);
  const listWidgetsBanca = sortedWidgets.filter(w => w.id === 'list_movimenti_banca' && w.enabled);
  const listWidgetsFatture = sortedWidgets.filter(w => ['list_fatture_incassare', 'list_fatture_pagare'].includes(w.id) && w.enabled);
  const listWidgetsOther = sortedWidgets.filter(w => ['list_costi_recenti', 'list_top_fornitori', 'list_clienti_rischio'].includes(w.id) && w.enabled);

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Panoramica della situazione finanziaria</p>
        </div>
        <Dialog open={showConfig} onOpenChange={setShowConfig}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-2"
              data-testid="customize-dashboard-btn"
            >
              <Settings2 className="w-4 h-4" />
              Personalizza
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Personalizza Dashboard</DialogTitle>
              <DialogDescription>
                Attiva o disattiva i widget e riordinali come preferisci
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 mt-4">
              {sortedWidgets.map((widget, index) => (
                <div 
                  key={widget.id}
                  className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-5 w-5 p-0"
                        onClick={() => moveWidget(widget.id, 'up')}
                        disabled={index === 0}
                      >
                        <GripVertical className="w-3 h-3 rotate-90" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-5 w-5 p-0"
                        onClick={() => moveWidget(widget.id, 'down')}
                        disabled={index === sortedWidgets.length - 1}
                      >
                        <GripVertical className="w-3 h-3 rotate-90" />
                      </Button>
                    </div>
                    <div>
                      <p className="font-medium text-[#1D1D1F]">{widget.name}</p>
                      <p className="text-xs text-[#86868B]">
                        {widget.size === 'sm' ? 'Piccolo' : widget.size === 'md' ? 'Medio' : 'Grande'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {widget.enabled ? (
                      <Eye className="w-4 h-4 text-[#34C759]" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-[#86868B]" />
                    )}
                    <Switch
                      checked={widget.enabled}
                      onCheckedChange={() => toggleWidget(widget.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <Button 
                variant="outline" 
                size="sm"
                onClick={resetWidgets}
                className="flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Ripristina Default
              </Button>
              <Button 
                onClick={saveWidgetConfig}
                disabled={savingConfig}
                className="bg-[#007AFF] hover:bg-[#0066CC]"
              >
                {savingConfig ? "Salvataggio..." : "Salva Configurazione"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Main KPI Cards */}
      {kpiWidgetsPrimary.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {kpiWidgetsPrimary.map(widget => {
            const Component = widgetComponents[widget.id];
            return Component ? <Component key={widget.id} /> : null;
          })}
        </div>
      )}

      {/* Secondary KPI Row */}
      {kpiWidgetsSecondary.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {kpiWidgetsSecondary.map(widget => {
            const Component = widgetComponents[widget.id];
            return Component ? <Component key={widget.id} /> : null;
          })}
        </div>
      )}

      {/* Charts Row */}
      {chartWidgets.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {chartWidgets.map(widget => {
            const Component = widgetComponents[widget.id];
            return Component ? <Component key={widget.id} /> : null;
          })}
        </div>
      )}

      {/* Fatture Row */}
      {listWidgetsFatture.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {listWidgetsFatture.map(widget => {
            const Component = widgetComponents[widget.id];
            return Component ? <Component key={widget.id} /> : null;
          })}
        </div>
      )}

      {/* Bank Movements Row */}
      {listWidgetsBanca.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {listWidgetsBanca.map(widget => {
            const Component = widgetComponents[widget.id];
            return Component ? <Component key={widget.id} /> : null;
          })}
        </div>
      )}

      {/* Bottom Row */}
      {listWidgetsOther.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {listWidgetsOther.map(widget => {
            const Component = widgetComponents[widget.id];
            return Component ? <Component key={widget.id} /> : null;
          })}
        </div>
      )}

      {/* Empty state if all widgets disabled */}
      {widgets.filter(w => w.enabled).length === 0 && (
        <div className="text-center py-16">
          <Settings2 className="w-16 h-16 text-[#86868B] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">Dashboard vuota</h3>
          <p className="text-[#86868B] mb-4">Tutti i widget sono disabilitati</p>
          <Button onClick={() => setShowConfig(true)} className="bg-[#007AFF]">
            Configura Widget
          </Button>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
